import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import ResultsPanel from '../../pages/Home/Search/components/SearchResults/ResultsPanel';
import useCanvasStore from '../../stores/canvasStore';
import { selectUser } from '../../redux/selectors/canvas.selectors';
import { usePagination } from '../../hooks/usePagination';
import { useFitCount } from '../../hooks/useFitCount';

/**
 * One bookmark view column: loads its list into canvasStore and pages it
 * by as many rows as fit in the column, in a search-style results panel.
 * @param {string} stateKey - canvasStore key holding the list (null while loading)
 * @param {string} itemsProp - Prop name the results component expects the items under
 * @param {{dateFrom, dateTo}} dateRange - Optional date range to fetch with (must be memoized)
 * @param {string} emptyTitle - Empty state title (replaced by "Loading..." while loading)
 * @param {string} emptyHint - Empty state hint
 * Remaining props (results, accent, title, callbacks) are passed through to ResultsPanel.
 */
const BookmarkViewColumn = ({ stateKey, itemsProp, dateRange, emptyTitle, emptyHint, ...panelProps }) => {
    const userId = useSelector(selectUser)?.user_id;
    const items = useCanvasStore((state) => state[stateKey]);
    const fetchBookmarkViewColumn = useCanvasStore((state) => state.fetchBookmarkViewColumn);

    useEffect(() => {
        if (userId) {
            fetchBookmarkViewColumn(stateKey, userId, dateRange);
        }
    }, [stateKey, userId, dateRange, fetchBookmarkViewColumn]);

    const isLoading = items === null;
    const isDateFiltered = Boolean(dateRange?.dateFrom || dateRange?.dateTo);
    const [bodyRef, pageSize] = useFitCount();
    const { pageItems, ...pagination } = usePagination(items || [], pageSize);

    return (
        <ResultsPanel
            {...panelProps}
            {...pagination}
            {...{ [itemsProp]: pageItems }}
            bodyRef={bodyRef}
            rowSize="comfortable"
            emptyTitle={isLoading ? 'Loading...' : emptyTitle}
            emptyHint={isLoading ? '' : (isDateFiltered ? 'Nothing in this date range' : emptyHint)}
        />
    );
};

export default BookmarkViewColumn;
